import React, {useState, useRef, useEffect} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {Mic, MicOff, Trash2, ArrowLeft, Volume2, VolumeX, Sparkles, Radio} from 'lucide-react';
import toast from 'react-hot-toast';
import useWebSocket from "../../hooks/useWebSocket";
import {useAudioRecorder} from "../../hooks/useAudioRecorder";
import {MessageType} from "../../enum";
import {TRANSCRIPTION_RATE} from "../../constants/transcription";
import {useUserStore} from "../../store/userStore";
import TranscriptionsLabView from "../../components/VoiceLab/Transcriptions";

const VoiceLab = () => {
    const navigate = useNavigate();

    /* -------------------- State -------------------- */
    const [isRecording, setIsRecording] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [transcriptions, setTranscriptions] = useState([]);
    const [selectedLanguage, setSelectedLanguage] = useState('zul');

    const [localStream, setLocalStream] = useState(null);
    const [callActive, setCallActive] = useState(false);
    const [audioLevel, setAudioLevel] = useState(0);

    /* -------------------- Refs -------------------- */
    const transcriptionsEndRef = useRef(null);

    /* -------------------- Identity -------------------- */
    const userId = useUserStore((state) => state.userId);
    const userName = useUserStore((state) => state.userName);
    const {roomCode} = useParams();

    /* -------------------- WebSocket -------------------- */
    const {
        isConnected,
        messages,
        sendMessage,
    } = useWebSocket(roomCode, userId);

    /* -------------------- Metadata -------------------- */
    const metadata = {
        room_code: roomCode,
        user_id: userId,
        channel: 0,
        translate: true,
        diarise: false,
        lang_code: selectedLanguage,
        target_lang: 'eng',
        sample_rate: TRANSCRIPTION_RATE
    };

    /* -------------------- Audio Recorder -------------------- */
    const {
        isSpeaking,
        audioStats,
        recordingState,
    } = useAudioRecorder(
        localStream,
        isMuted,
        callActive,
        metadata,
        sendMessage,
        {
            onAudioLevel: (level) => {
                setAudioLevel(Math.min(100, Math.floor(level)));
            },
        }
    );

    /* -------------------- Handle incoming transcriptions -------------------- */
    useEffect(() => {
        if (!messages.length) return;

        const msg = messages[messages.length - 1];

        switch (msg.type || msg.msg_type) {
            case MessageType.TRANSCRIPT:
            case 'transcription':
                setTranscriptions(prev => [
                    ...prev,
                    {
                        id: crypto.randomUUID(),
                        transcript: msg.transcript,
                        translation: msg.translation,
                        confidence: msg.confidence ?? 1,
                        timestamp: new Date().toISOString(),
                    },
                ]);
                break;

            case 'transcription_status':
                console.log(msg)
                if (msg.status === 'disconnected') {
                    toast.error('Transcription service disconnected. Audio may not be transcribed.');
                } else if (msg.status === 'connected') {
                    toast.success('Transcription service connected');
                }
                break;

            case "pong":
                console.log(`Transcription status: ${msg.transcription_status}`);
                break


            default:
                console.log('[Room] Unhandled message:', msg.type || msg.msg_type);
        }

    }, [messages]);

    /* ---------- Auto-scroll ---------- */
    useEffect(() => {
        transcriptionsEndRef.current?.scrollIntoView({behavior: 'smooth'});
    }, [transcriptions, transcriptionsEndRef]);

    /* -------------------- Recording controls -------------------- */
    const startRecording = async () => {
        try {
            if (!isConnected) {
                toast.error('Not connected to transcription service');
                return;
            }

            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    sampleRate: TRANSCRIPTION_RATE,
                    channelCount: 1,
                    echoCancellation: false,
                    noiseSuppression: false,
                    autoGainControl: false,
                },
            });

            setLocalStream(stream);
            setCallActive(true);
            setIsRecording(true);

            toast.success('Recording started', {
                icon: '🎤',
                position: 'bottom-center',
            });
        } catch (err) {
            console.error(err);
            toast.error('Microphone access denied');
        }
    };

    const stopRecording = () => {
        localStream?.getTracks().forEach(t => t.stop());
        setLocalStream(null);
        setCallActive(false);
        setIsRecording(false);
        setIsMuted(false);

        toast.success('Recording stopped', {
            icon: '⏹️',
            position: 'bottom-center',
        });
    };

    const toggleMute = () => {
        setIsMuted(prev => !prev);
        toast.success(isMuted ? 'Unmuted' : 'Muted', {
            icon: isMuted ? '🔊' : '🔇',
            position: 'bottom-center',
        });
    };

    const clearTranscriptions = () => {
        if (window.confirm('Clear all transcriptions?')) {
            setTranscriptions([]);
            toast.success('Cleared', {icon: '🗑️'});
        }
    };

    /* -------------------- Languages -------------------- */
    const languages = [
        {code: 'zul', name: 'isiZulu', emoji: '🇿🇦'},
        {code: 'eng', name: 'English', emoji: '🇬🇧'},
        {code: 'afr', name: 'Afrikaans', emoji: '🇿🇦'},
        {code: 'xho', name: 'isiXhosa', emoji: '🇿🇦'},
    ];

    /* -------------------- UI -------------------- */
    return (
        <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-black flex flex-col">
            {/* Animated background */}
            <div
                className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(139,92,246,0.1),transparent_50%)]"/>
            <div
                className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(236,72,153,0.1),transparent_50%)]"/>

            {/* Header */}
            <header className="relative glass-panel border-b border-white/10 bg-white shadow-md border-gray-200">
                <div className=" mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/lobby')}
                            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                            title="Back to Lobby"
                        >
                            <ArrowLeft className="w-5 h-5 text-gray-600"/>
                        </button>

                        <img
                            src="/img/logo.png"
                            alt="Vulavula Co-Pilot Logo"
                            className="h-16 lg:h-24 w-auto object-contain"
                        />

                        <div className="flex items-center gap-3">
                            <div
                                className="w-12 h-12 bg-accent-secondary rounded-xl flex items-center justify-center">
                                <Sparkles className="w-6 h-6 text-white"/>
                            </div>
                            <div>
                                <h1 className="text-2xl font-display font-bold bg-accent-secondary bg-clip-text text-transparent">
                                    Voice Lab
                                </h1>
                                <p className="text-xs lg:text-sm text-gray-600">Experiment with voice transcription</p>
                            </div>
                        </div>
                    </div>

                   {/* Language selector */}
                    <div className="flex items-center gap-3 pr-4">
                        <div className="border border-gray-400 bg-white px-4 py-2 rounded-lg">
                            <select
                                value={selectedLanguage}
                                onChange={(e) => setSelectedLanguage(e.target.value)}
                                disabled={isRecording}
                                className="bg-transparent text-gray-900 text-sm focus:outline-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {languages.map(lang => (
                                    <option key={lang.code} value={lang.code} className="bg-white">
                                        {lang.emoji} {lang.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main content */}
            <div className="relative flex-1 flex flex-col max-w-4xl mx-auto w-full p-6">
                {/* Status indicator */}
                <div className="glass-panel rounded-2xl p-6 mb-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            {/* Visual audio level indicator */}
                            <div className="relative w-16 h-16">
                                <div className={`absolute inset-0 rounded-full transition-all duration-300 ${
                                    isSpeaking
                                        ? 'bg-gradient-to-br from-purple-500 to-pink-600 animate-pulse'
                                        : 'bg-gradient-to-br from-zinc-700 to-zinc-800'
                                }`}>
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        {isRecording ? (
                                            isMuted ? (
                                                <MicOff className="w-8 h-8 text-white"/>
                                            ) : (
                                                <Mic className="w-8 h-8 text-white"/>
                                            )
                                        ) : (
                                            <Radio className="w-8 h-8 text-white/50"/>
                                        )}
                                    </div>
                                </div>

                                {/* Animated rings when speaking */}
                                {isSpeaking && (
                                    <>
                                        <div
                                            className="absolute inset-0 rounded-full border-2 border-purple-500 animate-ping"/>
                                        <div
                                            className="absolute inset-0 rounded-full border-2 border-pink-500 animate-ping"
                                            style={{animationDelay: '0.3s'}}/>
                                    </>
                                )}
                            </div>

                            <div>
                                <div className="text-white font-semibold text-lg">
                                    {!isRecording ? 'Ready to Record' : isMuted ? 'Recording (Muted)' : isSpeaking ? 'Speaking...' : 'Listening...'}
                                </div>
                                <div className="text-zinc-400 text-sm">
                                    {!isRecording
                                        ? 'Click the button below to start'
                                        : `Transcribing in ${languages.find(l => l.code === selectedLanguage)?.name}`
                                    }
                                </div>
                            </div>
                        </div>

                        {/* Audio level bars */}
                        {isRecording && (
                            <div className="flex items-center gap-1">
                                {[...Array(20)].map((_, i) => (
                                    <div
                                        key={i}
                                        className={`w-1 rounded-full transition-all duration-100 ${
                                            audioLevel > i * 5
                                                ? 'bg-gradient-to-t from-purple-500 to-pink-500 h-8'
                                                : 'bg-zinc-700 h-2'
                                        }`}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Transcriptions area */}
                <div className="glass-panel rounded-2xl flex-1 flex flex-col overflow-hidden">
                    <div className="border-b border-white/10 p-4 flex items-center justify-between">
                        <div className="flex items-center gap-2 text-white">
                            <Volume2 className="w-5 h-5"/>
                            <span className="font-semibold">Transcriptions</span>
                            <span className="text-xs text-zinc-400 ml-2">({transcriptions.length})</span>
                        </div>

                        {transcriptions.length > 0 && (
                            <button
                                onClick={clearTranscriptions}
                                className="flex items-center gap-2 px-3 py-1.5 text-sm text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                            >
                                <Trash2 className="w-4 h-4"/>
                                Clear
                            </button>
                        )}
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-thin">
                        <TranscriptionsLabView
                            transcriptions={transcriptions}
                            transcriptionsEndRef={transcriptionsEndRef}
                        />
                    </div>
                </div>

                {/* Controls */}
                <div className="glass-panel rounded-2xl p-6 mt-6">
                    <div className="flex items-center justify-center gap-4">
                        {!isRecording ? (
                            <button
                                onClick={startRecording}
                                className="relative px-12 py-5 rounded-2xl font-semibold text-lg transition-all duration-300 shadow-2xl hover:shadow-3xl hover:scale-105 active:scale-95 bg-gradient-to-r from-accent-secondary via-accent-primary to-accent-tertiary hover:from-accent-primary hover:via-accent-tertiary hover:to-accent-secondary text-white"
                            >
                                <div
                                    className="absolute inset-0 rounded-2xl bg-gradient-to-r from-accent-secondary to-accent-primary to-accent-tertiary animate-pulse"/>
                                <div className="relative flex items-center gap-3">
                                    <Mic className="w-6 h-6"/>
                                    <span>Start Recording</span>
                                </div>
                            </button>
                        ) : (
                            <>
                                <button
                                    onClick={toggleMute}
                                    className={`px-8 py-4 rounded-xl font-medium transition-all hover:scale-105 active:scale-95 ${
                                        isMuted
                                            ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                                            : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                                    }`}
                                >
                                    <div className="flex items-center gap-2">
                                        {isMuted ? <VolumeX className="w-5 h-5"/> : <Volume2 className="w-5 h-5"/>}
                                        <span>{isMuted ? 'Unmute' : 'Mute'}</span>
                                    </div>
                                </button>

                                <button
                                    onClick={stopRecording}
                                    className="px-12 py-5 rounded-2xl font-semibold text-lg transition-all duration-300 shadow-2xl hover:shadow-3xl hover:scale-105 active:scale-95 bg-gradient-to-r from-red-500 via-rose-600 to-red-600 hover:from-red-600 hover:via-rose-700 hover:to-red-700 text-white"
                                >
                                    <div className="flex items-center gap-3">
                                        <MicOff className="w-6 h-6"/>
                                        <span>Stop Recording</span>
                                    </div>
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}


export default VoiceLab;